import { CampaignStatus, VideoStatus } from 'src/Constants/enums'
import { EventDismissReasonsEnum } from 'src/Redux/creatorSubmittedSoundsApi'

import type { ID } from './common'
import type {
  Genre,
  Language,
  Track,
  Mood,
  ProperGenreOrLanguage,
} from './tracks'
import type { Review } from './reviews'
import type { Payment, PaypalPayment } from './payments'

export enum CampaignPlatformTypeEnum {
  SPOTIFY = 'spotify',
  TIKTOK = 'tiktok',
  REELS = 'instagram',
  SPOTIFY_TIKTOK = 'spotify+tiktok',
  META = 'ads',
}

export enum CampaignStatusEnum {
  PENDING_APPROVAL = 'pending_approval',
  APPROVED = 'approved',
  SCHEDULED = 'scheduled',
  RUNNING = 'running',
  ENDED = 'ended',
  STOPPED_BY_ADMIN = 'stopped_by_admin',
  AWAITING_REVIEW = 'awaiting_review',
  PENDING = 'pending',
  INPROCESS = 'in_process',
  FINISHED = 'finished',
  CANCELED = 'canceled',
  CANCELED_BY_ADMIN = 'canceled_by_admin',
  STOPPED = 'stopped',
}

export enum CampaignTypeEnum {
  FIRST = 'first',
  BASIC = 'basic',
  RELAUNCHED = 'relaunched',
  NEW = 'new',
}

export enum TiktokCampaignStatus {
  AWAITING_REVIEW = 'awaiting_review',
  PENDING = 'pending',
  SCHEDULED = 'scheduled',
  RUNNING = 'running',
  IN_PROCESS = 'in_process',
  ENDED = 'ended',
  CANCELED = 'canceled',
  FINISHED = 'finished',
  STOPPED = 'stopped',
  STOPPED_BY_ADMIN = 'stopped_by_admin',
  CANCELED_BY_ADMIN = 'canceled_by_admin',
}

export type TiktokLyricsPlatform = 'Musixmatch' | 'Genius' | 'LyricFind'

export enum SpotifyCampaignMatchingType {
  FEED = 'feed',
  MANUAL = 'manual',
}

export enum SpotifyCampaignAiUsageEnum {
  NONE = 'none',
  ASSISTED = 'assisted',
  GENERATED = 'generated',
}

export enum MatchingTypeEnum {
  SMART = 'smart',
  CURATED = 'curated',
}

export enum TikTokCampaignPricingModelEnum {
  FollowersBased = 'followers_based',
  ViewsBased = 'views_based',
  VideosBased = 'videos_based',
}

export enum ActionsForSelectGenresAndLanguagesEnum {
  SELECT_OPTION = 'select-option',
}

export enum RelaunchInvokePlacement {
  DETAILS = 'details',
  LIST = 'list',
}

export interface Coupon {
  id: number
  discount: number
  value: string
}

export interface Campaign {
  id: ID
  status: CampaignStatus
  start_date: string | Date
  end_date: string | Date
  endDate: string | Date
  startDate: string | Date
  type: CampaignTypeEnum
  Reviews: Array<Review>
  Track: Track
  PaypalPayments: Array<PaypalPayment>
  relaunched: boolean
  price?: number
  trackId?: number
  playlists?: number
  max_playlists?: number
  curators_count?: number
  followers?: number
  isCurrent?: boolean
  moods?: Array<number>
  artistName?: string
  trackName?: number
  category: string
  genres?: Array<Genre>
  languages?: Array<Language>
  created_at: string
  platformType: CampaignPlatformTypeEnum
  reviewedReviews: number
  allReview: number
  reviewed_reviews: number
  all_review: number
  trackCoverUrl: string
  trackTitle: string
  trackAuthorName: string
  is_feed?: boolean
  submittedVideosAmount?: number
  influencersAcceptedAmount?: number
  createdAt?: string | Date
  amount?: string
  current_amount?: string
}

export interface CampaignToSchedule {
  id: string | number
  status: CampaignStatusEnum
  type: string
  genres: ProperGenreOrLanguage[]
  languages: ProperGenreOrLanguage[]
  start_date: Date | string
  moods?: Array<Mood>
  playlistsCount?: number
  curatorsCount?: number
  minPlaylistsCount?: number
  absoluteCuratorsCount: number
  is_feed: boolean
  track: {
    title: string
    author: string
    cover: string
  }
  payment?: unknown
  trackDuration?: number
  matchingType?: SpotifyCampaignMatchingType | null
  trackNote?: string | null
  aiUsage?: SpotifyCampaignAiUsageEnum | null
}

export interface CampaignStatusResponse {
  campaign: CampaignToSchedule
  payment: Payment
}

export interface TiktokCampaign {
  hasNotCompletedEvents?: boolean
  id: string | number
  type: CampaignTypeEnum
  pricingModel?: TikTokCampaignPricingModelEnum | null
  trackCoverUrl?: string
  trackDuration: number
  trackTitle: string
  trackId: number
  trackAuthorName: string
  startDate?: string | Date
  endDate: string | Date
  status: TiktokCampaignStatus
  platformType: CampaignPlatformTypeEnum.TIKTOK
  amount?: number
  isCurrent: boolean
  comment?: string
  lyricsLink?: string | null
  audience?: {
    maxFollowers: number
    maxVideos: number
    minFollowers: number
    minVideos: number
  }
  is_feed?: boolean
  totalVideos: number
  totalViews: number
  trackNewVideosCount?: number
  trackCommentsCount?: number
  totalShares: number
  relaunched: boolean
  track?: {
    url?: string
    duration: number
    coverUrl?: string
    title?: string
    trackId: number
    authorName: string
    playUrl?: string
  }
  allReview?: number
  reviewedReviews?: number
  submittedVideosAmount: number
  influencersAcceptedAmount: number
  reviews: { done: number; influencersAcceptedCount: number }
  start_date?: string | Date
  trackTiktokId?: string | number
  createdAt?: string | number | Date
  updatedAt?: string
  expectedMaxVideos?: number
  expectedMinVideos?: number
  stats?: {
    totalVideos: number
    totalLikes: number
    totalShares: number
    totalViews: number
    totalComments: number
  }
  statsUpdatedAt?: string
  totalRefunded?: number
  currentAmount?: number
  userId?: number
  reserved: number
  spent: number
  commentType: string
}

export interface GetGtmInfoInterface {
  campaignType: string
  campaignId: number
  genresList: [string]
  languagesList: [string]
  transactionId: string
  value: number
  resultPrice: number
  campaignNumber: number
  purchaseType: string
  isFeed?: boolean
}

export interface CampaignVideo {
  id: number
  createdAt: string
  updatedAt: string
  status: VideoStatus
  tiktokCampaignId: number
  videoLink: string
  videoUploadDate: string | Date
  nickname: string
  rate: number
  stars: number
  followerCount: number
  userPhotoUrl?: string
  tiktokProfileUrl: string
  feedback: string
  viewsCount?: number
  deadlineToLoadVideo?: string
  reward?: number
  views: number
  isConfirmed: boolean
  isAvailable: boolean
  preModerationDate: string
  minViewsDeadline: string
  minViews: number
  pricingModel: TikTokCampaignPricingModelEnum
  dismissReason: EventDismissReasonsEnum
  availabilityCaseOpenAt: string | null
  loweredViewsCaseOpenAt: string | null
  comment?: string
  commentType?: string
}

export interface SpotifyCampaign {
  createdAt?: string | number | Date
  id: number
  trackCoverUrl?: string
  trackTitle?: string
  endDate?: string | Date
  trackAuthorName?: string
  platformType: CampaignPlatformTypeEnum.SPOTIFY
  status: CampaignStatusEnum
  type: CampaignTypeEnum
  startDate?: string | Date
  trackId?: number
  genres?: Array<Genre>
  languages?: Array<Language>
  price?: number
  relaunched?: boolean
  amount?: string | null
  trackName?: string
  artistName?: string
  followers?: number
  max_followers?: number
  playlists?: number
  max_playlists?: number
  parent_campaign_id?: number
  isCurrent?: boolean
  comment?: string
  curators_count?: number
  isMinPrice?: boolean
  matchingType?: string
  isMaxPrice?: boolean
  audience?: {
    followers: number
    curators: number
    maxCurators: number
    maxFollowers: number
    maxPlaylists: number
    minPlaylists: number
    playlists: number
  }
  loading?: boolean
  overrun_budget?: boolean
  reviewedReviews?: number
  allReview?: number
  is_feed?: boolean
  Track?: Track
  track?: Track
  start_date?: string | Date
  end_date?: string | Date
  current_amount?: string | null
  submittedVideosAmount?: number
  influencersAcceptedAmount?: number
  isPreRelease?: boolean
  releaseDate?: string
  placementsCount?: number
  trackSpotifyId?: string | null
}

export interface TikTokUpdateCampaign {
  id: number
  comment?: string
  startDate?: string | Date
  commentType?: string
  lyricsLink?: string | null
  pricingModel?: TikTokCampaignPricingModelEnum
}
